// CommunityRoutesPanel - Browse and search community caliche routes
// Shows all user-contributed routes with voting, comments, and navigation

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Collapse,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Schedule as TimeIcon,
  Straighten as DistanceIcon,
  TrendingUp as PopularIcon,
  AccessTime as RecentIcon,
  Route as RouteIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  LocalGasStation as WellIcon,
  Water as SwdIcon,
  Construction as RigIcon,
  Google as GoogleIcon,
  ChatBubbleOutline as CommentIcon,
  AddAPhoto as AddAPhotoIcon,
  // Apple as AppleIcon, // Removed - only Google Maps supports waypoints
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { CyberSpinner } from './CyberSpinner';
import { telegram } from '../services/telegram';
import { getCyberColors } from '../theme/cyberColors';
import { useAppStore } from '../stores/appStore';

interface CommunityRoute {
  id: string;
  facility_id: string;
  facility_type: string;
  user_id: string;
  name: string;
  description: string;
  start_description: string;
  upvotes: number;
  downvotes: number;
  score: number;
  created_at: string;
  route_data: {
    distanceKm?: number;
    durationMin?: number;
    path?: { lat: number; lng: number }[];
  };
  waypoint_count: number;
  comment_count: number;
  userVote: number; // -1, 0, or 1
  landmarks?: Array<{
    landmark_id: string;
    route_id: string;
    sequence_order: number;
    landmark_type: string;
    latitude: number;
    longitude: number;
    name?: string;
    description?: string;
  }>;
}

interface RouteComment {
  id: number;
  route_id: string;
  user_id: string;
  comment: string;
  created_at: string;
}

interface CommunityRoutesPanelProps {
  open: boolean;
  onClose: () => void;
  onViewOnMap?: (route: CommunityRoute) => void;
  onAddPhoto?: (route: { id: string; name: string; type: string }) => void;
  refreshKey?: number;
}

const API_BASE = '/api';

// Facility type icon mapping
const FACILITY_ICONS: Record<string, React.ReactElement> = {
  well: <WellIcon />,
  wells: <WellIcon />,
  swd: <SwdIcon />,
  swds: <SwdIcon />,
  rig: <RigIcon />,
  rigs: <RigIcon />,
};

const FACILITY_COLORS: Record<string, string> = {
  well: '#f59e0b',
  wells: '#f59e0b',
  swd: '#10b981',
  swds: '#10b981',
  rig: '#fbbf24',
  rigs: '#fbbf24',
};

export const CommunityRoutesPanel: React.FC<CommunityRoutesPanelProps> = ({
  open,
  onClose,
  onAddPhoto,
  refreshKey,
}) => {
  const darkMode = useAppStore((s) => s.darkMode);
  const highContrast = useAppStore((s) => s.highContrast);
  const cyber = getCyberColors(darkMode, highContrast);

  const [routes, setRoutes] = useState<CommunityRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'distance' | 'mine'>('popular');
  const [expandedRoute, setExpandedRoute] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, RouteComment[]>>({});
  const [loadingComments, setLoadingComments] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // 4-Letter Drilldown State
  const [selectedLetter, setSelectedLetter] = useState('');
  const [selectedSecondLetter, setSelectedSecondLetter] = useState('');
  const [selectedThirdLetter, setSelectedThirdLetter] = useState('');
  const [selectedFourthLetter, setSelectedFourthLetter] = useState('');
  const [letterStage, setLetterStage] = useState(1);
  const [letterExpanded, setLetterExpanded] = useState(false);

  const userId = telegram.getUserId();

  // Helper: Get letter key for drilldown
  const getLetterKey = (char: string): string => {
    if (!char) return '';
    const upper = char.toUpperCase();
    return /[A-Z]/.test(upper) ? upper : '';
  };

  // Letter counts computation (1st letter)
  const letterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    routes.forEach(route => {
      const key = getLetterKey(route.name?.[0] || '');
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [routes]);

  // 2nd letter counts
  const secondCounts = useMemo(() => {
    if (!selectedLetter) return {};
    const counts: Record<string, number> = {};
    routes.forEach(route => {
      const first = getLetterKey(route.name?.[0] || '');
      if (first === selectedLetter) {
        const key = getLetterKey(route.name?.[1] || '');
        if (key) counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [routes, selectedLetter]);

  // 3rd letter counts
  const thirdCounts = useMemo(() => {
    if (!selectedLetter || !selectedSecondLetter) return {};
    const counts: Record<string, number> = {};
    routes.forEach(route => {
      const first = getLetterKey(route.name?.[0] || '');
      const second = getLetterKey(route.name?.[1] || '');
      if (first === selectedLetter && second === selectedSecondLetter) {
        const key = getLetterKey(route.name?.[2] || '');
        if (key) counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [routes, selectedLetter, selectedSecondLetter]);

  // 4th letter counts
  const fourthCounts = useMemo(() => {
    if (!selectedLetter || !selectedSecondLetter || !selectedThirdLetter) return {};
    const counts: Record<string, number> = {};
    routes.forEach(route => {
      const first = getLetterKey(route.name?.[0] || '');
      const second = getLetterKey(route.name?.[1] || '');
      const third = getLetterKey(route.name?.[2] || '');
      if (first === selectedLetter && second === selectedSecondLetter && third === selectedThirdLetter) {
        const key = getLetterKey(route.name?.[3] || '');
        if (key) counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [routes, selectedLetter, selectedSecondLetter, selectedThirdLetter]);

  // Filtered routes based on selected letters
  const filteredRoutes = useMemo(() => {
    return routes.filter(route => {
      const first = getLetterKey(route.name?.[0] || '');
      if (selectedLetter && first !== selectedLetter) return false;
      
      const second = getLetterKey(route.name?.[1] || '');
      if (selectedSecondLetter && second !== selectedSecondLetter) return false;
      
      const third = getLetterKey(route.name?.[2] || '');
      if (selectedThirdLetter && third !== selectedThirdLetter) return false;
      
      const fourth = getLetterKey(route.name?.[3] || '');
      if (selectedFourthLetter && fourth !== selectedFourthLetter) return false;
      
      return true;
    });
  }, [routes, selectedLetter, selectedSecondLetter, selectedThirdLetter, selectedFourthLetter]);

  // Fetch routes from API
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        search,
        sortBy: sortBy === 'mine' ? 'recent' : sortBy, // Use recent sort for "mine"
        limit: '50',
        userId: userId || '',
      });
      
      // Add filter for user's routes if "mine" is selected
      if (sortBy === 'mine' && userId) {
        params.set('createdBy', userId);
      }
      
      const response = await fetch(`${API_BASE}/landmark-routes?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Map API response to match our interface
        const mappedRoutes = data.routes.map((route: any) => ({
          id: route.route_id,
          facility_id: route.facility_id,
          facility_type: 'well', // Default for now
          user_id: route.created_by_user_id,
          name: route.name,
          description: route.description || '',
          start_description: '',
          upvotes: route.upvotes || 0,
          downvotes: route.downvotes || 0,
          score: (route.upvotes || 0) - (route.downvotes || 0),
          created_at: route.created_at,
          route_data: {
            distanceKm: route.total_distance_meters ? route.total_distance_meters / 1000 : 0,
            durationMin: route.estimated_duration_minutes || 0,
          },
          waypoint_count: route.landmark_count || 0,
          comment_count: 0,
          userVote: route.user_vote || 0,
          landmarks: route.landmarks || [],
        }));
        setRoutes(mappedRoutes);
      } else {
        throw new Error(data.error || 'Failed to load routes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load routes');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, userId]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      fetchRoutes();
    }, 300);
    return () => clearTimeout(timer);
  }, [open, search, sortBy, fetchRoutes, refreshKey]);

  // Fetch comments for a route
  const fetchComments = async (routeId: string) => {
    if (comments[routeId]) return; // Already loaded
    
    try {
      setLoadingComments(routeId);
      const response = await fetch(`${API_BASE}/route-comments?routeId=${routeId}`);
      const data = await response.json();
      
      if (data.success) {
        setComments(prev => ({ ...prev, [routeId]: data.comments }));
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(null);
    }
  };

  // Vote on a route
  const handleVote = async (routeId: string, vote: 1 | -1) => {
    if (!userId) {
      setError('Please log in to vote');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/landmark-routes/${routeId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, vote }),
      });
      
      const data = await response.json();
      
      console.log('[Vote] Response:', data);
      
      if (data.success) {
        // Update local state
        setRoutes(prev => prev.map(r => {
          if (r.id === routeId) {
            const oldVote = r.userVote;
            const newUpvotes = r.upvotes + (vote === 1 ? (oldVote === 1 ? 0 : 1) : (oldVote === 1 ? -1 : 0));
            const newDownvotes = r.downvotes + (vote === -1 ? (oldVote === -1 ? 0 : 1) : (oldVote === -1 ? -1 : 0));
            return {
              ...r,
              upvotes: newUpvotes,
              downvotes: newDownvotes,
              score: newUpvotes - newDownvotes,
              userVote: oldVote === vote ? 0 : vote,
            };
          }
          return r;
        }));
        telegram.hapticFeedback('light');
      } else {
        setError(data.error || 'Failed to vote');
      }
    } catch (err) {
      console.error('[Vote] Error:', err);
      setError('Failed to vote: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Delete a route (only for owner)
  const handleDeleteRoute = async (routeId: string) => {
    if (!userId) {
      setError('Please log in to delete');
      return;
    }

    try {
      console.log('[Delete] Deleting route:', routeId, 'userId:', userId);
      
      const response = await fetch(`${API_BASE}/landmark-routes/${routeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
        },
      });
      
      console.log('[Delete] Response status:', response.status);
      
      const data = await response.json();
      console.log('[Delete] Response data:', data);
      
      if (data.success) {
        // Remove from local state
        setRoutes(prev => prev.filter(r => r.id !== routeId));
        setSuccess('Route deleted');
        telegram.hapticFeedback('success');
      } else {
        console.error('[Delete] Error from API:', data.error);
        setError(data.error || 'Failed to delete route');
      }
    } catch (err) {
      console.error('[Delete] Exception:', err);
      setError('Failed to delete route: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Submit a comment
  const handleSubmitComment = async (routeId: string) => {
    if (!newComment.trim() || !userId) return;

    try {
      setSubmittingComment(true);
      const response = await fetch(`${API_BASE}/route-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routeId,
          userId,
          comment: newComment.trim(),
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setNewComment('');
        // Reload comments
        setComments(prev => ({ ...prev, [routeId]: undefined as any }));
        fetchComments(routeId);
        // Update comment count
        setRoutes(prev => prev.map(r => 
          r.id === routeId ? { ...r, comment_count: r.comment_count + 1 } : r
        ));
        setSuccess('Comment added');
        telegram.hapticFeedback('success');
      }
    } catch (err) {
      setError('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Toggle route expansion
  const handleExpandRoute = (routeId: string) => {
    if (expandedRoute === routeId) {
      setExpandedRoute(null);
    } else {
      setExpandedRoute(routeId);
      fetchComments(routeId);
    }
  };

  // Format distance
  const formatDistance = (km?: number) => {
    if (!km) return '—';
    if (km < 1) return `${Math.round(km * 1000)}m`;
    return `${km.toFixed(1)}km`;
  };

  // Format time
  const formatTime = (min?: number) => {
    if (!min) return '—';
    if (min < 60) return `${Math.round(min)}min`;
    const hours = Math.floor(min / 60);
    const mins = Math.round(min % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Format date
  const formatDate = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return d.toLocaleDateString();
  };

  // Drilldown handlers
  const handleLetterClick = (letter: string) => {
    if (letterStage === 1) {
      setSelectedLetter(letter);
      setLetterStage(2);
    } else if (letterStage === 2) {
      setSelectedSecondLetter(letter);
      setLetterStage(3);
    } else if (letterStage === 3) {
      setSelectedThirdLetter(letter);
      setLetterStage(4);
    } else if (letterStage === 4) {
      setSelectedFourthLetter(letter);
    }
    telegram.hapticFeedback('light');
  };

  const handleClearLetters = () => {
    setSelectedLetter('');
    setSelectedSecondLetter('');
    setSelectedThirdLetter('');
    setSelectedFourthLetter('');
    setLetterStage(1);
    telegram.hapticFeedback('light');
  };

  const handlePrevStage = () => {
    if (letterStage === 2) {
      setSelectedLetter('');
      setLetterStage(1);
    } else if (letterStage === 3) {
      setSelectedSecondLetter('');
      setLetterStage(2);
    } else if (letterStage === 4) {
      setSelectedThirdLetter('');
      setLetterStage(3);
    }
    telegram.hapticFeedback('light');
  };

  const handleNextStage = () => {
    if (letterStage < 4) {
      setLetterStage(letterStage + 1);
      telegram.hapticFeedback('light');
    }
  };

  // Get current counts based on stage
  const getCurrentCounts = () => {
    if (letterStage === 1) return letterCounts;
    if (letterStage === 2) return secondCounts;
    if (letterStage === 3) return thirdCounts;
    return fourthCounts;
  };

  // Get breadcrumb text
  const getBreadcrumb = () => {
    if (!selectedLetter) return 'All';
    let breadcrumb = selectedLetter;
    if (selectedSecondLetter) breadcrumb += ` > ${selectedSecondLetter}`;
    if (selectedThirdLetter) breadcrumb += ` > ${selectedThirdLetter}`;
    if (selectedFourthLetter) breadcrumb += ` > ${selectedFourthLetter}`;
    return `${breadcrumb} (${filteredRoutes.length})`;
  };

  // Get stage label
  const getStageLabel = () => {
    if (letterStage === 1) return '1st';
    if (letterStage === 2) return '2nd';
    if (letterStage === 3) return '3rd';
    return '4th';
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: cyber.bgPrimary,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: `1px solid ${cyber.borderDefault}`,
          bgcolor: cyber.bgSecondary,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <RouteIcon sx={{ color: cyber.neonCyan, fontSize: 28 }} />
        <Typography variant="h6" sx={{ flex: 1, color: cyber.textPrimary, fontWeight: 600 }}>
          🛣️ Caliche Routes
        </Typography>
        <IconButton onClick={onClose} sx={{ color: cyber.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </Paper>

      {/* Search & Sort */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${cyber.borderDefault}` }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search routes by destination..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: cyber.textSecondary }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: cyber.bgPrimary,
              color: cyber.textPrimary,
              '& fieldset': { borderColor: cyber.borderDefault },
              '&:hover fieldset': { borderColor: cyber.neonCyan },
            },
          }}
          sx={{ mb: 1.5 }}
        />
        
        <ToggleButtonGroup
          value={sortBy}
          exclusive
          onChange={(_, v) => v && setSortBy(v)}
          size="small"
          fullWidth
        >
          <ToggleButton
            value="mine"
            sx={{
              color: sortBy === 'mine' ? cyber.neonCyan : cyber.textSecondary,
              borderColor: cyber.borderDefault,
              '&.Mui-selected': { bgcolor: `${cyber.neonCyan}22` },
            }}
          >
            <RouteIcon sx={{ mr: 0.5, fontSize: 18 }} /> My Routes
          </ToggleButton>
          <ToggleButton
            value="popular"
            sx={{
              color: sortBy === 'popular' ? cyber.neonCyan : cyber.textSecondary,
              borderColor: cyber.borderDefault,
              '&.Mui-selected': { bgcolor: `${cyber.neonCyan}22` },
            }}
          >
            <PopularIcon sx={{ mr: 0.5, fontSize: 18 }} /> Popular
          </ToggleButton>
          <ToggleButton
            value="recent"
            sx={{
              color: sortBy === 'recent' ? cyber.neonCyan : cyber.textSecondary,
              borderColor: cyber.borderDefault,
              '&.Mui-selected': { bgcolor: `${cyber.neonCyan}22` },
            }}
          >
            <RecentIcon sx={{ mr: 0.5, fontSize: 18 }} /> Recent
          </ToggleButton>
          <ToggleButton
            value="distance"
            sx={{
              color: sortBy === 'distance' ? cyber.neonCyan : cyber.textSecondary,
              borderColor: cyber.borderDefault,
              '&.Mui-selected': { bgcolor: `${cyber.neonCyan}22` },
            }}
          >
            <DistanceIcon sx={{ mr: 0.5, fontSize: 18 }} /> Shortest
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 4-Letter Drilldown Controls */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: cyber.bgSecondary,
          borderBottom: `1px solid ${cyber.borderDefault}`,
          p: 2,
        }}
      >
        {/* Breadcrumb & Controls Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: letterExpanded ? 2 : 0 }}>
          {/* Breadcrumb */}
          <Box
            sx={{
              flex: 1,
              bgcolor: cyber.bgPrimary,
              border: `1px solid ${cyber.borderDefault}`,
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: cyber.textPrimary,
                fontWeight: 500,
                fontSize: 14,
                flex: 1,
              }}
            >
              {getBreadcrumb()}
            </Typography>
          </Box>

          {/* Clear Button */}
          {selectedLetter && (
            <IconButton
              onClick={handleClearLetters}
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#f97316',
                color: '#fff',
                border: '1px solid #f97316',
                borderRadius: 1,
                '&:hover': {
                  bgcolor: '#ea580c',
                  borderColor: '#ea580c',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Expand/Collapse Button */}
          <IconButton
            onClick={() => {
              setLetterExpanded(!letterExpanded);
              telegram.hapticFeedback('light');
            }}
            sx={{
              width: 44,
              height: 44,
              bgcolor: cyber.bgPrimary,
              border: `1px solid ${cyber.borderDefault}`,
              borderRadius: 1,
              color: cyber.neonCyan,
              '&:hover': {
                bgcolor: `${cyber.neonCyan}22`,
                borderColor: cyber.neonCyan,
              },
            }}
          >
            {letterExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 24 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 24 }} />
            )}
          </IconButton>
        </Box>

        {/* Letter Navigation (Collapsible) */}
        <Collapse in={letterExpanded}>
          <Box>
            {/* Navigation Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              {/* Prev Stage Button */}
              <IconButton
                onClick={handlePrevStage}
                disabled={letterStage === 1}
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: cyber.bgPrimary,
                  border: `1px solid ${cyber.borderDefault}`,
                  borderRadius: 1,
                  color: cyber.neonCyan,
                  '&:hover': {
                    bgcolor: `${cyber.neonCyan}22`,
                    borderColor: cyber.neonCyan,
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    color: cyber.textSecondary,
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 32 }} />
              </IconButton>

              {/* Stage Label */}
              <Box
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  bgcolor: cyber.bgPrimary,
                  border: `1px solid ${cyber.borderDefault}`,
                  borderRadius: 1,
                  py: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: cyber.neonCyan,
                    fontWeight: 600,
                    fontSize: 18,
                  }}
                >
                  {getStageLabel()} Letter
                </Typography>
              </Box>

              {/* Next Stage Button */}
              <IconButton
                onClick={handleNextStage}
                disabled={letterStage === 4}
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: cyber.bgPrimary,
                  border: `1px solid ${cyber.borderDefault}`,
                  borderRadius: 1,
                  color: cyber.neonCyan,
                  '&:hover': {
                    bgcolor: `${cyber.neonCyan}22`,
                    borderColor: cyber.neonCyan,
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    color: cyber.textSecondary,
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>

            {/* Letter Chips Grid */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => {
                const count = getCurrentCounts()[letter] || 0;
                if (count === 0) return null; // Auto-hide zero-count letters
                
                return (
                  <Chip
                    key={letter}
                    label={`${letter} (${count})`}
                    onClick={() => handleLetterClick(letter)}
                    sx={{
                      height: 32,
                      fontSize: 14,
                      fontWeight: 600,
                      bgcolor: cyber.bgPrimary,
                      color: cyber.neonCyan,
                      border: `1px solid ${cyber.neonCyan}60`,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: `${cyber.neonCyan}22`,
                        borderColor: cyber.neonCyan,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Routes List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CyberSpinner size={40} />
          </Box>
        ) : filteredRoutes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <RouteIcon sx={{ fontSize: 60, color: cyber.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography color="textSecondary">
              {search ? 'No routes match your search' : 'No caliche routes yet'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Be the first to share a caliche route!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredRoutes.map((route) => {
              const facilityColor = FACILITY_COLORS[route.facility_type] || '#22c55e';
              const landmarks = route.landmarks || [];
              
              return (
                <Box
                  key={route.id}
                  onClick={() => handleExpandRoute(route.id)}
                  sx={{
                    backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '12px',
                    boxShadow: `0 6px 16px ${facilityColor}25, 0 4px 12px rgba(0,0,0,0.12)`,
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 12px 28px ${facilityColor}40, 0 8px 20px rgba(0,0,0,0.18)`,
                    }
                  }}
                >
                  {/* ROW 1: Icon + Route Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: `${facilityColor}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {FACILITY_ICONS[route.facility_type] || <RouteIcon sx={{ fontSize: 18, color: facilityColor }} />}
                    </Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: cyber.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '1.05rem',
                        lineHeight: 1.25,
                        flex: 1
                      }}
                    >
                      {route.name || `Route to ${route.facility_id}`}
                    </Typography>
                  </Box>

                  {/* ROW 2: Chips - Distance, Duration, Date */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.75, 
                    flexWrap: 'wrap', 
                    alignItems: 'center',
                    overflow: 'hidden',
                    pb: 0.5,
                  }}>
                    <Chip
                      icon={<DistanceIcon sx={{ fontSize: '0.75rem !important' }} />}
                      label={formatDistance(route.route_data?.distanceKm)}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.1)',
                        color: darkMode ? '#60a5fa' : '#1e40af',
                        '& .MuiChip-icon': { color: darkMode ? '#60a5fa' : '#1e40af' },
                      }}
                    />
                    <Chip
                      icon={<TimeIcon sx={{ fontSize: '0.75rem !important' }} />}
                      label={formatTime(route.route_data?.durationMin)}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(180, 83, 9, 0.1)',
                        color: darkMode ? '#fbbf24' : '#b45309',
                        '& .MuiChip-icon': { color: darkMode ? '#fbbf24' : '#b45309' },
                      }}
                    />
                    <Chip
                      label={formatDate(route.created_at)}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                        backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                        color: darkMode ? '#34d399' : '#065f46',
                      }}
                    />
                    {route.description && (
                      <Chip
                        label={route.description.slice(0, 25) + (route.description.length > 25 ? '...' : '')}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                          color: darkMode ? '#ffffff' : '#334155',
                          maxWidth: 160,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                        }}
                      />
                    )}
                  </Box>

                  {/* ROW 3: Action Buttons */}
                  <Box 
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, pt: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Upvote */}
                    <Tooltip title="Good route!" arrow>
                      <Badge badgeContent={route.upvotes} color="success" sx={{ '& .MuiBadge-badge': { bgcolor: '#22c55e', fontWeight: 600 } }}>
                        <IconButton
                          size="small"
                          onClick={() => handleVote(route.id, 1)}
                          sx={{
                            width: 40,
                            height: 40,
                            color: route.userVote === 1 ? '#22c55e' : cyber.textMuted,
                            backgroundColor: route.userVote === 1 ? 'rgba(34, 197, 94, 0.2)' : 'transparent',
                            '&:hover': { color: '#22c55e', backgroundColor: 'rgba(34, 197, 94, 0.2)' }
                          }}
                        >
                          <ThumbUpIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      </Badge>
                    </Tooltip>

                    {/* Downvote */}
                    <Tooltip title="Bad route" arrow>
                      <Badge badgeContent={route.downvotes} color="error" sx={{ '& .MuiBadge-badge': { bgcolor: '#ef4444', fontWeight: 600 } }}>
                        <IconButton
                          size="small"
                          onClick={() => handleVote(route.id, -1)}
                          sx={{
                            width: 40,
                            height: 40,
                            color: route.userVote === -1 ? '#ef4444' : cyber.textMuted,
                            backgroundColor: route.userVote === -1 ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                            '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.2)' }
                          }}
                        >
                          <ThumbDownIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      </Badge>
                    </Tooltip>

                    {/* Comments Button */}
                    <Tooltip title="View/Add Comments" arrow>
                      <Badge badgeContent={route.comment_count || 0} color="info" sx={{ '& .MuiBadge-badge': { bgcolor: '#3b82f6', fontWeight: 600 } }}>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRoute(route.id)}
                          sx={{
                            width: 40,
                            height: 40,
                            color: expandedRoute === route.id ? '#3b82f6' : cyber.textMuted,
                            backgroundColor: expandedRoute === route.id ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                            '&:hover': { color: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.2)' }
                          }}
                        >
                          <CommentIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      </Badge>
                    </Tooltip>

                    {/* Google Maps - Uses all waypoints from the caliche route */}
                    <Tooltip title="Navigate with Google Maps" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (landmarks.length > 0) {
                            // Sort landmarks by sequence order to get the correct path
                            const sortedLandmarks = [...landmarks].sort((a, b) => a.sequence_order - b.sequence_order);
                            
                            // Last landmark is always the destination
                            const destination = sortedLandmarks[sortedLandmarks.length - 1];
                            
                            // Get all waypoints except the destination
                            const allWaypoints = sortedLandmarks.slice(0, -1);
                            
                            // Smart sampling: pick evenly-spaced waypoints if more than 10
                            // Google Maps URL supports max 10 waypoints
                            let selectedWaypoints: typeof allWaypoints = [];
                            
                            if (allWaypoints.length <= 10) {
                              // Use all waypoints if 10 or fewer
                              selectedWaypoints = allWaypoints;
                            } else {
                              // Smart sampling: pick 10 evenly-spaced waypoints
                              // This ensures the route follows the actual caliche road path
                              const step = allWaypoints.length / 10;
                              for (let i = 0; i < 10; i++) {
                                const index = Math.floor(i * step);
                                selectedWaypoints.push(allWaypoints[index]);
                              }
                            }
                            
                            // Build waypoints string for Google Maps URL
                            const waypointCoords = selectedWaypoints
                              .map(lm => `${lm.latitude},${lm.longitude}`)
                              .join('|');
                            
                            // Build the full Google Maps directions URL
                            let mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination.latitude},${destination.longitude}&travelmode=driving`;
                            if (waypointCoords) {
                              mapsUrl += `&waypoints=${waypointCoords}`;
                            }
                            
                            window.open(mapsUrl, '_blank');
                            telegram.hapticFeedback('light');
                          }
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          color: '#4285F4',
                          backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.15)' : 'rgba(66, 133, 244, 0.1)',
                          '&:hover': { backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.3)' : 'rgba(66, 133, 244, 0.2)' }
                        }}
                      >
                        <GoogleIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Tooltip>

                    {/* Add Photo - Document road conditions */}
                    <Tooltip title="Add road photo" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (onAddPhoto) {
                            onAddPhoto({
                              id: route.id,
                              name: route.name,
                              type: 'caliche_route'
                            });
                            telegram.hapticFeedback('light');
                          }
                        }}
                        disabled={!onAddPhoto}
                        sx={{
                          width: 40,
                          height: 40,
                          color: onAddPhoto ? '#a21caf' : '#6b7280',
                          backgroundColor: darkMode ? 'rgba(162, 28, 175, 0.15)' : 'rgba(162, 28, 175, 0.1)',
                          opacity: onAddPhoto ? 1 : 0.5,
                          '&:hover': { backgroundColor: darkMode ? 'rgba(162, 28, 175, 0.3)' : 'rgba(162, 28, 175, 0.2)' }
                        }}
                      >
                        <AddAPhotoIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Tooltip>

                    {/* Delete - only for owner */}
                    {route.user_id === userId && (
                      <Tooltip title="Delete route" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm('Delete this route? This cannot be undone.')) {
                              handleDeleteRoute(route.id);
                            }
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            color: cyber.textMuted,
                            '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.2)' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Expanded Comments Section */}
                  <Collapse in={expandedRoute === route.id}>
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1.5,
                        borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: cyber.textSecondary, display: 'block', mb: 1 }}
                      >
                        💬 Comments ({route.comment_count})
                      </Typography>

                      {loadingComments === route.id ? (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                          <CyberSpinner size={24} />
                        </Box>
                      ) : (comments[route.id] || []).length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{ color: cyber.textSecondary, py: 1, fontStyle: 'italic' }}
                        >
                          No comments yet. Be the first!
                        </Typography>
                      ) : (
                        <Box sx={{ mb: 1 }}>
                          {(comments[route.id] || []).map((comment) => (
                            <Box
                              key={comment.id}
                              sx={{
                                py: 1,
                                borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <Typography variant="body2" sx={{ color: cyber.textPrimary }}>
                                {comment.comment}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: cyber.textSecondary }}
                              >
                                {formatDate(comment.created_at)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Add Comment */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSubmitComment(route.id);
                            }
                          }}
                          InputProps={{
                            sx: {
                              bgcolor: darkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(241, 245, 249, 0.8)',
                              color: cyber.textPrimary,
                              borderRadius: '8px',
                              '& fieldset': { borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
                            },
                          }}
                        />
                        <IconButton
                          onClick={() => handleSubmitComment(route.id)}
                          disabled={!newComment.trim() || submittingComment}
                          sx={{
                            color: '#22c55e',
                            '&:disabled': { color: cyber.textSecondary },
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunityRoutesPanel;
